import inventoryCatalog from "../../data/inventoryCatalog.json";
import type { InventoryCatalogCategorySchema } from "../../data/types";
import { InventoryCategories } from "../enums";
// import {
//   formatUsageUnitShortLabel,
//   getUsageTrackProfile,
//   type AssetUsageUnit,
//   type SpaceUsageUnit,
//   type UsageMeasurementUnit,
// } from "./inventoryUsageTracking";

export type PricingArticle = {
  id: string;
  title: string;
  source: string;
  url: string;
  description: string;
};

export type MarketBenchmarkRow = {
  id: string;
  label: string;
  range: string;
  context: string;
};

export type ProductPriceRecommendation = {
  price: number;
  source: "category" | "regional" | "item";
};

export type ProductPriceRecommendationDetail = ProductPriceRecommendation & {
  locationLabel: string;
  methodLabel: string;
  dataSourceLabel: string;
  guidanceSummary: string;
  marketRows: MarketBenchmarkRow[];
  articles: PricingArticle[];
};

type RegionalMarket = {
  label: string;
  categoryMultiplier: number;
  productArticles: PricingArticle[];
};

const CATEGORY_BASE_PRICES: Record<string, number> = {
  equipment: 12,
  utensils: 8,
  appliances: 15,
  cleaning: 9,
  packaging: 7,
  disposables: 6,
  vegetables: 4,
  fruits: 4,
  grains: 3.5,
  pasta: 3.5,
  dairy: 5.2,
  meat: 8.5,
  poultry: 7.5,
  seafood: 9,
  bread: 4.5,
  bakery: 5,
  beverages: 6,
  alcohol: 12,
  snacks: 5.5,
  spices: 8,
  herbs: 6,
  condiments: 5,
  sauces: 5.5,
  oils: 7,
  fats: 6.5,
  sweets: 6,
  desserts: 7,
  frozen: 5.5,
  canned: 4,
  jarred: 4.5,
  baking: 5,
  services: 10,
  miscellaneous: 5,
};

const ASSET_USAGE_BASE_PRICES: Record<AssetUsageUnit, number> = {
  hour: 28,
  kwh: 0.18,
  btu: 0.012,
};

const SPACE_USAGE_BASE_PRICES: Record<SpaceUsageUnit, number> = {
  sq_ft: 2.5,
  cu_ft: 1.8,
  shelves: 45,
  pallets: 65,
};

const CATEGORY_MEASURE_UNITS: Record<string, string> = {
  equipment: "hr",
  appliances: "hr",
  utensils: "ea",
  cleaning: "ea",
  packaging: "ea",
  disposables: "ea",
  vegetables: "lb",
  fruits: "lb",
  grains: "lb",
  pasta: "lb",
  dairy: "ea",
  meat: "lb",
  poultry: "lb",
  seafood: "lb",
  bread: "ea",
  bakery: "ea",
  beverages: "ea",
  alcohol: "bottle",
  snacks: "ea",
  spices: "oz",
  herbs: "oz",
  condiments: "bottle",
  sauces: "bottle",
  oils: "bottle",
  fats: "lb",
  sweets: "ea",
  desserts: "ea",
  frozen: "ea",
  canned: "ea",
  jarred: "ea",
  baking: "lb",
  services: "month",
  miscellaneous: "ea",
};

const REGIONAL_MARKETS: Record<string, RegionalMarket> = {
  dc: {
    label: "Washington, DC",
    categoryMultiplier: 1.08,
    productArticles: [
      {
        id: "dc-product-1",
        title: "How DC shared kitchens price inventory items",
        source: "HomeCook Resource Pantry",
        url: "https://site.homecookapp.com/blog",
        description:
          "A practical overview of what food businesses in the DC metro area charge for common kitchen products and supplies.",
      },
      {
        id: "dc-product-2",
        title: "Small food business pricing in the Mid-Atlantic",
        source: "Kitchen Cooperative Network",
        url: "https://site.homecookapp.com/blog",
        description:
          "Benchmark ranges for ingredients, packaging, and equipment usage fees collected from regional operators.",
      },
    ],
  },
  ca: {
    label: "Oakland, CA",
    categoryMultiplier: 1.18,
    productArticles: [
      {
        id: "ca-product-1",
        title: "Bay Area kitchen product pricing benchmarks",
        source: "HomeCook Resource Pantry",
        url: "https://site.homecookapp.com/blog",
        description:
          "Typical unit prices Bay Area kitchens list for ingredients, packaging, and shared equipment.",
      },
      {
        id: "ca-product-2",
        title: "California cottage food and commissary pricing trends",
        source: "West Coast Food Incubator Alliance",
        url: "https://site.homecookapp.com/blog",
        description:
          "Public survey data on what producers charge and what entrepreneurs expect to pay.",
      },
    ],
  },
  national: {
    label: "your region",
    categoryMultiplier: 1,
    productArticles: [
      {
        id: "nat-product-1",
        title: "National benchmarks for kitchen product pricing",
        source: "HomeCook Resource Pantry",
        url: "https://site.homecookapp.com/blog",
        description:
          "Aggregated public pricing signals from shared kitchens and food incubators across the U.S.",
      },
      {
        id: "nat-product-2",
        title: "Pricing products entrepreneurs actually book",
        source: "Kitchen Cooperative Network",
        url: "https://site.homecookapp.com/blog",
        description:
          "What to charge for staples, packaging, and add-ons without underpricing your kitchen.",
      },
    ],
  },
};

const round = (value: number, decimals = 2): number => {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
};

export const getMeasureUnitLabel = (category: string): string => {
  const normalizedCategory = category?.trim().toLowerCase() || "miscellaneous";
  return CATEGORY_MEASURE_UNITS[normalizedCategory] ?? CATEGORY_MEASURE_UNITS.miscellaneous;
};

export const getResourceLocationLabel = (address?: string): string => {
  if (!address) return REGIONAL_MARKETS.national.label;

  if (/washington|district of columbia|\bDC\b/i.test(address)) {
    return REGIONAL_MARKETS.dc.label;
  }
  if (/oakland|san francisco|california|\bCA\b/i.test(address)) {
    return REGIONAL_MARKETS.ca.label;
  }
  if (/new york|\bNY\b/i.test(address)) {
    return "New York, NY";
  }
  if (/austin|texas|\bTX\b/i.test(address)) {
    return "Austin, TX";
  }

  const parts = address.split(",").map((part) => part.trim()).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[parts.length - 2]}, ${parts[parts.length - 1]}`;
  }

  return address;
};

const getRegionalMarket = (address?: string): RegionalMarket => {
  const locationLabel = getResourceLocationLabel(address);
  if (locationLabel.includes("Washington") || locationLabel.includes("DC")) {
    return REGIONAL_MARKETS.dc;
  }
  if (locationLabel.includes("Oakland") || locationLabel.includes("CA")) {
    return REGIONAL_MARKETS.ca;
  }
  return { ...REGIONAL_MARKETS.national, label: locationLabel };
};

const getCategoryLabel = (category: string): string => {
  if (!category) return "inventory";
  return InventoryCategories[category as keyof typeof InventoryCategories] ?? category;
};

const findCatalogProductName = (category: string, productName?: string): string | null => {
  if (!productName?.trim()) return null;

  const catalogCategory = (inventoryCatalog as InventoryCatalogCategorySchema[]).find(
    (entry) => entry.category === category
  );
  if (!catalogCategory?.items?.length) return null;

  const normalized = productName.trim().toLowerCase();
  return catalogCategory.items.find((item) => item.name.toLowerCase() === normalized)?.name ?? null;
};

const buildCategoryMarketRows = (
  category: string,
  market: RegionalMarket,
  unitLabel: string
): MarketBenchmarkRow[] => {
  const base = CATEGORY_BASE_PRICES[category] ?? CATEGORY_BASE_PRICES.miscellaneous;
  const mult = market.categoryMultiplier;
  const categoryLabel = getCategoryLabel(category);

  return [
    {
      id: `${category}-1`,
      label: `${categoryLabel} category average`,
      range: `$${round(base * mult * 0.85)}–$${round(base * mult * 1.2)}/${unitLabel}`,
      context: `${market.label} kitchens`,
    },
    {
      id: `${category}-2`,
      label: "Comparable listings",
      range: `$${round(base * mult * 0.75)}–$${round(base * mult * 1.35)}/${unitLabel}`,
      context: "Regional operators",
    },
  ];
};

export const getProductPriceRecommendationDetail = (
  resourceAddress: string | undefined,
  category: string,
  productName?: string
): ProductPriceRecommendationDetail => {
  const market = getRegionalMarket(resourceAddress);
  const normalizedCategory = category || "miscellaneous";
  const catalogMatch = findCatalogProductName(normalizedCategory, productName);
  const base = CATEGORY_BASE_PRICES[normalizedCategory] ?? CATEGORY_BASE_PRICES.miscellaneous;
  const price = round(base * market.categoryMultiplier);
  const categoryLabel = getCategoryLabel(normalizedCategory);
  const unitLabel = getMeasureUnitLabel(normalizedCategory);

  if (catalogMatch) {
    return {
      price,
      source: "item",
      locationLabel: market.label,
      methodLabel: "Catalog + regional adjustment",
      dataSourceLabel: "Product catalog & public kitchen pricing data",
      guidanceSummary: `Based on typical ${catalogMatch} pricing in ${market.label} shared kitchens, adjusted for your region.`,
      marketRows: [
        {
          id: "item-1",
          label: catalogMatch,
          range: `$${round(price * 0.9)}–$${round(price * 1.15)}/${unitLabel}`,
          context: `${market.label} operators`,
        },
        ...buildCategoryMarketRows(normalizedCategory, market, unitLabel),
      ],
      articles: market.productArticles,
    };
  }

  return {
    price,
    source: "category",
    locationLabel: market.label,
    methodLabel: "Regional market benchmark",
    dataSourceLabel: "Public kitchen & supplier pricing data",
    guidanceSummary: productName?.trim()
      ? `No exact match for "${productName.trim()}" - using ${categoryLabel.toLowerCase()} benchmarks for ${market.label}.`
      : `Based on what comparable kitchens and food businesses in ${market.label} publicly list for ${categoryLabel.toLowerCase()} items.`,
    marketRows: buildCategoryMarketRows(normalizedCategory, market, unitLabel),
    articles: market.productArticles,
  };
};

/** Space vs shared allocation when tracking space/storage usage. */
export type SpaceAllocation = "dedicated" | "shared";

/** Usage units for equipment / asset-style inventory. */
export type AssetUsageUnit = "hour" | "kwh" | "btu";

/** Usage units for space / storage-style inventory. */
export type SpaceUsageUnit = "sq_ft" | "cu_ft" | "shelves" | "pallets";

export type UsageMeasurementUnit = AssetUsageUnit | SpaceUsageUnit;

export type UsageTrackProfile = "asset" | "space_storage";

/** Category keys that use space/storage usage units (future-ready). */
export const SPACE_STORAGE_CATEGORY_KEYS = ["space", "storage"] as const;

export const ASSET_USAGE_UNITS: { value: AssetUsageUnit; label: string }[] = [
  { value: "hour", label: "Hourly (hrs)" },
  { value: "kwh", label: "Electricity (kWh)" },
  { value: "btu", label: "Gas (BTU)" },
];

export const SPACE_USAGE_UNITS: { value: SpaceUsageUnit; label: string }[] = [
  { value: "sq_ft", label: "Square Feet (sq ft)" },
  { value: "cu_ft", label: "Cubic Feet (cu ft)" },
  { value: "shelves", label: "Shelves" },
  { value: "pallets", label: "Pallets" },
];

export const SPACE_ALLOCATION_OPTIONS: {
  value: SpaceAllocation;
  label: string;
  description: string;
}[] = [
  {
    value: "dedicated",
    label: "Dedicated",
    description:
      "Exclusive use during a booking - full consumption is attributed to that session.",
  },
  {
    value: "shared",
    label: "Shared",
    description:
      "Used alongside other members - consumption is estimated at 50% for shared sessions.",
  },
];

/** Returns the human-readable description for a space allocation mode. */
export const getSpaceAllocationDescription = (allocation: SpaceAllocation): string =>
  SPACE_ALLOCATION_OPTIONS.find((option) => option.value === allocation)?.description ?? "";

/** Resolves whether a category tracks as an asset or as space/storage. */
export const getUsageTrackProfile = (category: string): UsageTrackProfile => {
  const normalized = category.trim().toLowerCase();
  if (
    SPACE_STORAGE_CATEGORY_KEYS.includes(
      normalized as (typeof SPACE_STORAGE_CATEGORY_KEYS)[number]
    )
  ) {
    return "space_storage";
  }
  return "asset";
};

/** Default usage measurement unit for a category. */
export const getDefaultUsageMeasureUnit = (category: string): UsageMeasurementUnit =>
  getUsageTrackProfile(category) === "space_storage" ? "sq_ft" : "hour";

/** Short label for usage unit display (e.g. price notes). */
export const formatUsageUnitShortLabel = (unit: UsageMeasurementUnit): string => {
  switch (unit) {
    case "hour":
      return "hr";
    case "kwh":
      return "kWh";
    case "btu":
      return "BTU";
    case "sq_ft":
      return "sq ft";
    case "cu_ft":
      return "cu ft";
    case "shelves":
      return "shelf";
    case "pallets":
      return "pallet";
    default:
      return unit;
  }
};

/** Usage unit options for the given category profile. */
export const getUsageMeasureUnitOptions = (
  category: string
): { value: UsageMeasurementUnit; label: string }[] =>
  getUsageTrackProfile(category) === "space_storage" ? SPACE_USAGE_UNITS : ASSET_USAGE_UNITS;



/** Builds a regional usage-cost recommendation for track-usage unit pricing. */
export const getUsagePriceRecommendationDetail = (
  resourceAddress: string | undefined,
  category: string,
  usageUnit: UsageMeasurementUnit
): ProductPriceRecommendationDetail => {
  const market = getRegionalMarket(resourceAddress);
  const profile = getUsageTrackProfile(category);
  const mult = market.categoryMultiplier;
  const categoryLabel = getCategoryLabel(category || "miscellaneous");
  const unitLabel = formatUsageUnitShortLabel(usageUnit);

  let price: number;
  let marketRows: MarketBenchmarkRow[];
  let guidanceSummary: string;

  if (profile === "space_storage") {
    const spaceUnit = usageUnit as SpaceUsageUnit;
    price = round((SPACE_USAGE_BASE_PRICES[spaceUnit] ?? 2) * mult);
    marketRows = [
      {
        id: "space-usage-1",
        label: `${categoryLabel} space usage`,
        range: `$${round(price * 0.85)}–$${round(price * 1.2)}/${unitLabel}`,
        context: `${market.label} facilities`,
      },
      {
        id: "space-usage-2",
        label: "Comparable storage rates",
        range: `$${round(price * 0.75)}–$${round(price * 1.35)}/${unitLabel}`,
        context: "Regional operators",
      },
    ];
    guidanceSummary = `Based on publicly listed storage and space allocation rates in ${market.label} for ${categoryLabel.toLowerCase()} items at a typical provider cost per ${unitLabel}.`;
  } else {
    const assetUnit = usageUnit as AssetUsageUnit;
    price = round((ASSET_USAGE_BASE_PRICES[assetUnit] ?? 10) * mult);
    marketRows = [
      {
        id: "asset-usage-1",
        label: `${categoryLabel} usage cost`,
        range: `$${round(price * 0.85)}–$${round(price * 1.2)}/${unitLabel}`,
        context: `${market.label} kitchens`,
      },
      {
        id: "asset-usage-2",
        label: "Comparable utility / equipment fees",
        range: `$${round(price * 0.75)}–$${round(price * 1.35)}/${unitLabel}`,
        context: "Regional operators",
      },
    ];
    guidanceSummary = `Based on equipment and utility usage fees listed by comparable kitchens in ${market.label} for ${categoryLabel.toLowerCase()} items at a typical provider cost per ${unitLabel}.`;
  }

  return {
    price,
    source: "category",
    locationLabel: market.label,
    methodLabel: "Regional market benchmark",
    dataSourceLabel: "Public kitchen usage & utility pricing data",
    guidanceSummary,
    marketRows,
    articles: market.productArticles,
  };
};
